import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Tooltip,
  Typography,
} from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { makeStyles } from '@material-ui/core/styles';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { GroupEntity, parseEntityRef } from '@backstage/catalog-model';
import { InfoCard, Link } from '@backstage/core-components';
import { resolveUserGroupRefs } from '@internal/backstage-catalog-utils';

/**
 * Combined membership + leadership view for a User entity. Replaces both the
 * stock EntityUserProfileCard's "team list" and the custom UserRoleBadges that
 * previously sat at the bottom of the user page — one row per team the user
 * belongs to, with their leadership role (EM/PM/Tech Lead/Architect) shown
 * inline as a chip, or "Member" when they belong without leading.
 */

const useStyles = makeStyles(theme => ({
  card: { height: '100%' },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0),
  },
  chip: {
    minWidth: 80,
    justifyContent: 'center',
  },
  memberChip: {
    minWidth: 80,
    justifyContent: 'center',
    color: theme.palette.text.disabled,
  },
  divider: { margin: 0 },
  teamName: {
    fontSize: '0.95rem',
    flex: 1,
    minWidth: 0,
  },
  primaryStar: {
    fontSize: '1.1rem',
    color: theme.palette.warning.main,
    flexShrink: 0,
  },
  empty: {
    fontStyle: 'italic',
    color: theme.palette.text.disabled,
    textAlign: 'center',
    padding: theme.spacing(2),
  },
}));

interface TeamRow {
  ref: string;
  name: string;
  role?: 'EM' | 'PM' | 'Tech Lead' | 'Architect';
  /** Sort priority — lower comes first. Leadership > membership > alphabetical. */
  sortKey: number;
}

const LEADERSHIP_ANNOTATIONS: Array<{ key: string; label: TeamRow['role']; priority: number }> = [
  { key: 'team/lead', label: 'EM', priority: 0 },
  { key: 'team/tech-lead', label: 'Tech Lead', priority: 1 },
  { key: 'team/pm', label: 'PM', priority: 2 },
  { key: 'team/architect', label: 'Architect', priority: 3 },
];

/** "group:default/team-foo" → "/catalog/default/group/team-foo" */
function groupRefToCatalogPath(ref: string): string {
  const { kind, namespace, name } = parseEntityRef(ref, {
    defaultKind: 'group',
    defaultNamespace: 'default',
  });
  return `/catalog/${namespace.toLowerCase()}/${kind.toLowerCase()}/${name}`;
}

export function EntityUserTeamsCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);

  const userRef = `user:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  // Injected dynamically by primaryTeamModule.ts at catalog-processing time
  // (PAT-678) — never written to the User entity's backing YAML.
  const primaryTeamRef = entity.metadata.annotations?.['user/primary-team'];

  const [teams, setTeams] = useState<TeamRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let cancelled = false;
    // Resolves membership via relations (spec.members-derived) AND
    // leadership annotations — the catalog never creates a relation for a
    // lead-only assignment, so relying on entity.relations alone would
    // silently drop e.g. a Function-level EM assignment from this list.
    resolveUserGroupRefs(userRef, catalogApi)
      .then(groupRefs => {
        if (cancelled || groupRefs.length === 0) return { items: [] as (GroupEntity | undefined)[], groupRefs };
        return catalogApi
          .getEntitiesByRefs({
            entityRefs: groupRefs,
            fields: ['metadata.name', 'metadata.title', 'metadata.annotations'],
          })
          .then(({ items }) => ({ items: items as (GroupEntity | undefined)[], groupRefs }));
      })
      .then(({ items, groupRefs }) => {
        if (cancelled) return;
        const rows: TeamRow[] = [];
        items.forEach((e, i) => {
          if (!e) return;
          const g = e;
          const ref = groupRefs[i];
          const annotations = g.metadata.annotations ?? {};
          const leadership = LEADERSHIP_ANNOTATIONS.find(
            l => annotations[l.key] === userRef,
          );
          rows.push({
            ref,
            name: g.metadata.title ?? g.metadata.name,
            role: leadership?.label,
            sortKey: leadership
              ? leadership.priority
              : 100,
          });
        });
        // Leaders first (priority 0-3), then members (100), each block alphabetical.
        rows.sort(
          (a, b) =>
            a.sortKey - b.sortKey || a.name.localeCompare(b.name),
        );
        setTeams(rows);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, userRef]);

  let body: JSX.Element;
  if (loading) {
    body = (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  } else if (teams.length === 0) {
    body = (
      <Typography className={classes.empty}>
        Not a member of any teams.
      </Typography>
    );
  } else {
    body = (
      <>
        {teams.map((t, idx) => (
          <Box key={t.ref}>
            {idx > 0 && <Divider className={classes.divider} />}
            <Box className={classes.row}>
              {t.role ? (
                <Chip
                  label={t.role}
                  size="small"
                  color="primary"
                  variant="outlined"
                  className={classes.chip}
                />
              ) : (
                <Chip
                  label="Member"
                  size="small"
                  variant="outlined"
                  className={classes.memberChip}
                />
              )}
              <Typography className={classes.teamName} component="div">
                <Link to={groupRefToCatalogPath(t.ref)}>{t.name}</Link>
              </Typography>
              {primaryTeamRef === t.ref && (
                <Tooltip title="Primary team">
                  <StarIcon className={classes.primaryStar} aria-label="Primary team" />
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </>
    );
  }

  return (
    <InfoCard title="Teams" variant="gridItem">
      {body}
    </InfoCard>
  );
}
